import {
  DocumentTextIcon,
  LinkIcon,
  CheckCircleIcon,
  LockClosedIcon,
  ShieldCheckIcon,
  ClipboardDocumentCheckIcon,
  CheckBadgeIcon,
  EyeIcon,
  CodeBracketIcon,
  ShieldExclamationIcon,
  KeyIcon,
  GlobeAltIcon,
} from "@heroicons/react/24/outline";

/**
 * Static map from a tool's color name to its FULL icon class string.
 *
 * 2026-07-04 tools-wide audit Tier 1 : the icon class used to be built
 * dynamically as `text-${tool.color}-600`. Tailwind's JIT compiler only
 * emits classes it can see as LITERAL strings in the source - a
 * template-interpolated class name is invisible to it, so every one of
 * these classes was purged from the production CSS and the tool icons
 * silently rendered in the default text color. Each class must appear
 * below as a complete literal ; do NOT refactor this back into string
 * interpolation, and when adding a tool with a new color, add its
 * literal entry here.
 */
export const TOOL_ICON_COLOR_CLASSES = {
  amber: "text-amber-600 dark:text-amber-400",
  blue: "text-link",
  cyan: "text-cyan-600 dark:text-cyan-400",
  emerald: "text-emerald-600 dark:text-emerald-400",
  green: "text-green-600 dark:text-green-400",
  indigo: "text-indigo-600 dark:text-indigo-400",
  orange: "text-orange-600 dark:text-orange-400",
  pink: "text-pink-600 dark:text-pink-400",
  purple: "text-purple-600 dark:text-purple-400",
  red: "text-red-600 dark:text-red-400",
  teal: "text-teal-600 dark:text-teal-400",
  violet: "text-violet-600 dark:text-violet-400",
};

/**
 * The site's ONE list of tools : the home page grid and the header's
 * Tools menu both render this array, and nothing else lists the tools.
 *
 * Operator ruling 2026-10-01 : the header carries a Tools menu again,
 * with API Documentation inside it. The menu removed on 2026-08-23 was a
 * second, hand-written list that drifted from the home grid (the
 * Issuance Tool was missing from it for weeks), so the reinstated one
 * reads this array instead : a card added, renamed or removed here
 * changes both places in the same edit.
 */
const tools = [
  // FIRST because it is the only tool on this page that ISSUES an SSL
  // Certificate ; every other card inspects, converts or checks one.
  {
    title: "Hosted CaaS ACME Issuance Tool",
    description:
      "Issue an SSL Certificate with Your Trustico® Certificate as a Service (CaaS) Credentials, Proving Domain Control by DNS or File",
    icon: CheckBadgeIcon,
    href: "/issuance",
    color: "amber",
  },
  {
    title: "CSR & Private Key Generator",
    description:
      "Generate a New Certificate Signing Request (CSR) and Private Key for SSL Certificates with Customizable Key Sizes and Algorithms",
    icon: DocumentTextIcon,
    href: "/csr",
    color: "blue",
  },
  {
    title: "CSR Decoder",
    description:
      "Decode and View the Contents of a Certificate Signing Request (CSR) Including Subject Information and Public Key Details",
    icon: EyeIcon,
    href: "/csr-decoder",
    color: "indigo",
  },
  {
    title: "SSL Certificate Chain Analyzer",
    description:
      "Analyze SSL Certificate Chains, Validate SSL Certificate Paths, and Check SSL Certificate Health with Detailed Reporting",
    icon: LinkIcon,
    href: "/chain",
    color: "green",
  },
  {
    title: "SSL Certificate & Private Key Matcher",
    description:
      "Verify if an SSL Certificate and Private Key Match Each Other by Comparing Their Cryptographic Signatures",
    icon: CheckCircleIcon,
    href: "/match",
    color: "purple",
  },
  {
    title: "SSL Certificate to PFX Converter",
    description:
      "Convert SSL Certificate and Private Key to a Password-Protected PFX (PKCS#12) File Format for Easy Deployment",
    icon: LockClosedIcon,
    href: "/pfx",
    color: "orange",
  },
  {
    title: "CAA Record Generator",
    // 2026-07-04 tools-wide overhaul (operator-approved), audit item
    // #17 reviewer sweep : the dual-CA (Sectigo® + Trustico®) wording
    // was removed from the product - CAAService.generateRecords is
    // Sectigo®-only - so this card no longer advertises Trustico®
    // records the tool cannot generate.
    description:
      "Generate CAA DNS Records for Sectigo® SSL Certificates to Control Which Certificate Authorities May Issue for Your Domain",
    icon: ShieldExclamationIcon,
    href: "/caa",
    color: "red",
  },
  {
    title: "CAA Record Checker",
    description:
      "Check Existing CAA (Certificate Authority Authorization) DNS Records for Any Domain to Verify Which CAs Are Authorized",
    icon: ShieldCheckIcon,
    href: "/caa-check",
    color: "pink",
  },
  {
    title: "DCV Checker",
    description:
      "Verify CNAME, TXT, HTTP and HTTPS Domain Control Validation Records and Confirm Correct DNS or File Configuration",
    icon: ClipboardDocumentCheckIcon,
    href: "/dcv-checker",
    color: "violet",
  },
  {
    title: "DNS Stamp Generator",
    description:
      "Generate and Decode DNS Stamps for Plain DNS, DNSCrypt, DNS-over-HTTPS, DNS-over-TLS and DNS-over-QUIC Resolvers",
    icon: GlobeAltIcon,
    href: "/dnsstamp",
    color: "emerald",
  },
  {
    title: "Strong Password Generator",
    description:
      "Generate Secure, Random Passwords with Customizable Length and Character Types for Maximum Security",
    icon: KeyIcon,
    href: "/password",
    color: "cyan",
  },
  {
    title: "API Documentation",
    description:
      "Access SSL Tools Programmatically Through Our REST API for Automation and Integration with Your Applications",
    icon: CodeBracketIcon,
    href: "/api-docs",
    color: "teal",
    // 2026-07-04 operator consistency directive : "Read More" is
    // generic link text, which the Trustico® style guide forbids ;
    // the label now names the destination.
    buttonText: "View Documentation",
  },
];

export default tools;
